/**
 * @file lib/git/git-fs.ts
 * @description isomorphic-gitを使用したGitファイルシステム
 * @author GFTD Co., Ltd.
 */

import git from 'isomorphic-git';
import http from 'isomorphic-git/http/web';
const LightningFS = require('@isomorphic-git/lightning-fs');

/**
 * Git設定インターフェース
 */
export interface GitConfig {
  /** リポジトリURL */
  url: string;
  /** Personal Access Token */
  token: string;
  /** 作成者情報 */
  author: {
    name: string;
    email: string;
  };
  /** ブランチ名 */
  branch?: string;
  /** ローカルディレクトリ名 */
  dir?: string;
}

/**
 * GitFileSystemクラス
 * isomorphic-gitを使用してGitリポジトリをファイルシステムとして操作
 */
export class GitFileSystem {
  private fs: any;
  private config: GitConfig;
  private dir: string;
  private currentBranch: string;
  private isInitialized = false;

  constructor(config: GitConfig) {
    this.config = config;
    this.dir = config.dir || '/repo';
    this.currentBranch = config.branch || 'main';
    
    // MemoryFSBackendを使用して、ブラウザ環境でも動作するように
    this.fs = new LightningFS('git-fs', {
      wipe: true // 初期化時にクリア
    });
  }

  /**
   * ディレクトリを再帰的に作成
   */
  private async ensureDir(dirPath: string): Promise<void> {
    try {
      await this.fs.promises.mkdir(dirPath);
    } catch (error: any) {
      if (error.code === 'ENOENT') {
        // 親ディレクトリが存在しない場合、親から作成
        const parentDir = dirPath.substring(0, dirPath.lastIndexOf('/'));
        if (parentDir && parentDir !== dirPath) {
          await this.ensureDir(parentDir);
          await this.fs.promises.mkdir(dirPath);
        }
      } else if (error.code !== 'EEXIST') {
        // EEXIST以外のエラーは再スロー
        throw error;
      }
    }
  }

  /**
   * リポジトリを初期化またはクローン
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // ディレクトリを作成
      await this.ensureDir(this.dir);

      // リポジトリをクローンまたは初期化
      try {
        await this.clone();
      } catch (error) {
        console.warn('Clone failed, initializing new repository:', error);
        await this.init();
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize GitFileSystem:', error);
      throw error;
    }
  }

  /**
   * リポジトリをクローン
   */
  private async clone(): Promise<void> {
    await git.clone({
      fs: this.fs,
      http,
      dir: this.dir,
      url: this.config.url,
      onAuth: () => ({
        username: 'oauth2',
        password: this.config.token,
      }),
      ref: this.currentBranch,
      singleBranch: false,
      depth: 1,
    });
  }

  /**
   * 新しいリポジトリを初期化
   */
  private async init(): Promise<void> {
    await git.init({
      fs: this.fs,
      dir: this.dir,
      defaultBranch: this.currentBranch,
    });

    // リモートを追加
    await git.addRemote({
      fs: this.fs,
      dir: this.dir,
      remote: 'origin',
      url: this.config.url,
    });
  }

  /**
   * ブランチを切り替え
   * @param branchName ブランチ名
   * @param createIfNotExists 存在しない場合は作成
   */
  async switchBranch(branchName: string, createIfNotExists: boolean = false): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // ブランチが存在するかチェック
      const branches = await git.listBranches({
        fs: this.fs,
        dir: this.dir,
      });

      if (!branches.includes(branchName)) {
        if (createIfNotExists) {
          // 新しいブランチを作成
          await git.branch({
            fs: this.fs,
            dir: this.dir,
            ref: branchName,
          });
        } else {
          throw new Error(`Branch "${branchName}" does not exist`);
        }
      }

      // ブランチをチェックアウト
      await git.checkout({
        fs: this.fs,
        dir: this.dir,
        ref: branchName,
      });

      this.currentBranch = branchName;
    } catch (error) {
      console.error('Failed to switch branch:', error);
      throw error;
    }
  }

  /**
   * ファイルを書き込み
   * @param filePath ファイルパス
   * @param content ファイル内容
   */
  async writeFile(filePath: string, content: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const fullPath = `${this.dir}/${filePath}`;
      
      // ディレクトリを作成（必要に応じて）
      const dirname = fullPath.substring(0, fullPath.lastIndexOf('/'));
      await this.ensureDir(dirname);

      // ファイルを書き込み
      await this.fs.promises.writeFile(fullPath, content, 'utf8');
    } catch (error) {
      console.error('Failed to write file:', error);
      throw error;
    }
  }

  /**
   * ファイルを読み込み
   * @param filePath ファイルパス
   * @returns ファイル内容
   */
  async readFile(filePath: string): Promise<string> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const fullPath = `${this.dir}/${filePath}`;
      const content = await this.fs.promises.readFile(fullPath, 'utf8');
      return content;
    } catch (error) {
      console.error('Failed to read file:', error);
      throw error;
    }
  }

  /**
   * ファイルを削除
   * @param filePath ファイルパス
   */
  async deleteFile(filePath: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const fullPath = `${this.dir}/${filePath}`;
      await this.fs.promises.unlink(fullPath);
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  }

  /**
   * ディレクトリの内容を一覧取得
   * @param dirPath ディレクトリパス
   * @returns ファイル・ディレクトリ一覧
   */
  async readDir(dirPath: string = ''): Promise<string[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const fullPath = dirPath ? `${this.dir}/${dirPath}` : this.dir;
      const entries = await this.fs.promises.readdir(fullPath);
      
      // .gitディレクトリを除外
      return entries.filter((entry: string) => entry !== '.git');
    } catch (error) {
      console.error('Failed to read directory:', error);
      throw error;
    }
  }

  /**
   * ファイルが存在するかチェック
   * @param filePath ファイルパス
   * @returns 存在するかどうか
   */
  async exists(filePath: string): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const fullPath = `${this.dir}/${filePath}`;
      await this.fs.promises.stat(fullPath);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * 変更をコミット
   * @param message コミットメッセージ
   * @param filePaths コミットするファイルパス（空の場合は全て）
   */
  async commit(message: string, filePaths?: string[]): Promise<string> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // ファイルをステージング
      if (filePaths && filePaths.length > 0) {
        for (const filePath of filePaths) {
          await git.add({
            fs: this.fs,
            dir: this.dir,
            filepath: filePath,
          });
        }
      } else {
        // 全ファイルを追加
        await git.add({
          fs: this.fs,
          dir: this.dir,
          filepath: '.',
        });
      }

      // コミット
      const sha = await git.commit({
        fs: this.fs,
        dir: this.dir,
        message,
        author: this.config.author,
      });

      return sha;
    } catch (error) {
      console.error('Failed to commit:', error);
      throw error;
    }
  }

  /**
   * リモートにプッシュ
   */
  async push(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await git.push({
        fs: this.fs,
        http,
        dir: this.dir,
        remote: 'origin',
        ref: this.currentBranch,
        onAuth: () => ({
          username: 'oauth2',
          password: this.config.token,
        }),
      });
    } catch (error) {
      console.error('Failed to push:', error);
      throw error;
    }
  }

  /**
   * リモートからプル
   */
  async pull(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await git.pull({
        fs: this.fs,
        http,
        dir: this.dir,
        ref: this.currentBranch,
        author: this.config.author,
        onAuth: () => ({
          username: 'oauth2',
          password: this.config.token,
        }),
      });
    } catch (error) {
      console.error('Failed to pull:', error);
      throw error;
    }
  }

  /**
   * ブランチ一覧を取得
   * @returns ブランチ一覧
   */
  async listBranches(): Promise<string[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      return await git.listBranches({
        fs: this.fs,
        dir: this.dir,
      });
    } catch (error) {
      console.error('Failed to list branches:', error);
      throw error;
    }
  }

  /**
   * 現在のブランチを取得
   * @returns 現在のブランチ名
   */
  getCurrentBranch(): string {
    return this.currentBranch;
  }

  /**
   * コミット履歴を取得
   * @param depth 取得する履歴の深さ
   * @returns コミット履歴
   */
  async getCommitHistory(depth: number = 10): Promise<any[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const commits = await git.log({
        fs: this.fs,
        dir: this.dir,
        depth,
      });

      return commits.map(commit => ({
        oid: commit.oid,
        message: commit.commit.message,
        author: commit.commit.author,
        committer: commit.commit.committer,
      }));
    } catch (error) {
      console.error('Failed to get commit history:', error);
      throw error;
    }
  }

  /**
   * 作業ディレクトリの状態を取得
   * @returns ファイルの状態一覧
   */
  async getStatus(): Promise<any[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const status = await git.statusMatrix({
        fs: this.fs,
        dir: this.dir,
      });

      return status.map(([filepath, headStatus, workdirStatus, stageStatus]) => ({
        filepath,
        headStatus,
        workdirStatus,
        stageStatus,
        status: this.getFileStatus(headStatus, workdirStatus, stageStatus),
      }));
    } catch (error) {
      console.error('Failed to get status:', error);
      throw error;
    }
  }

  /**
   * ファイルの状態を判定
   */
  private getFileStatus(headStatus: number, workdirStatus: number, stageStatus: number): string {
    if (headStatus === 1 && workdirStatus === 2 && stageStatus === 2) return 'modified';
    if (headStatus === 0 && workdirStatus === 2 && stageStatus === 2) return 'added';
    if (headStatus === 1 && workdirStatus === 0 && stageStatus === 0) return 'deleted';
    if (headStatus === 1 && workdirStatus === 1 && stageStatus === 1) return 'unmodified';
    if (headStatus === 0 && workdirStatus === 2 && stageStatus === 0) return 'untracked';
    return 'unknown';
  }

  /**
   * リソースのクリーンアップ
   */
  async cleanup(): Promise<void> {
    try {
      // 必要に応じてリソースをクリーンアップ
      this.isInitialized = false;
    } catch (error) {
      console.error('Failed to cleanup GitFileSystem:', error);
      throw error;
    }
  }
}

export default GitFileSystem; 